import "./locationPhysicianAddComponent.scss";
import FormTitleWithNavigationBack
    from "../../../../shared/components/form-title-with-navigation-back/FormTitleWithNavigationBack";
import React, {useCallback, useEffect, useState} from "react";
import SelectDropdownComponent from "../../../../shared/components/core/select-dropdown/SelectDropdownComponent";
import CommonService from "../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import {ImageConfig} from "../../../../constants";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import PhysicianDetailsComponent from "../../../../shared/components/physician-details/PhysicianDetailsComponent";
import AccordionComponent from "../../../../shared/components/accordion/accordionComponent";
import _ from "lodash";
import FeedbackComponent from "../../../../shared/components/feedback/feedbackComponent";
import LottieFileGenarationComponent
    from "../../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {useSearchParams} from "react-router-dom";

interface LocationPhysicianAddComponentProps {
    locationId: number;
    next: Function;
    cancel: Function;
    prev: Function;
    facilityId: number;
}

const LocationPhysicianAddComponent = (props: LocationPhysicianAddComponentProps) => {
        const {locationId, facilityId, cancel, next, prev} = props;
        const [physicianId, setPhysicianId] = useState([]);
        const [isLinking, setIsLinking] = useState(false);
        const [physicianList, setPhysicianList] = useState<any>([]);
        const [completePhysicianList, setCompletePhysicianList] = useState<any>([]);
        const [isPhysicianListLoading, setIsPhysicianListLoading] = useState<boolean>(false);
        const [isPhysicianListLoaded, setIsPhysicianListLoaded] = useState<boolean>(false);
        const [isPhysicianListLoadingFailed, setIsPhysicianListLoadingFailed] = useState<boolean>(false);
        const [physiciansLinkedToLocation, setPhysiciansLinkedToLocation] = useState<any>([]);
        const [searchParams] = useSearchParams();
        const lastCompletedStep = searchParams.get("lastCompletedStep");

        const getPhysicianList = useCallback(() => {
            const payload = {};
            setIsPhysicianListLoading(true);
            setIsPhysicianListLoaded(false);
            setIsPhysicianListLoadingFailed(false);
            CommonService._facility.getPhysicianList(facilityId, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response.data) {
                        setPhysicianList(response.data);
                        setCompletePhysicianList(response.data);
                    }
                    setIsPhysicianListLoading(false);
                    setIsPhysicianListLoaded(true);
                })
                .catch((error: any) => {
                    setIsPhysicianListLoaded(true);
                    setIsPhysicianListLoadingFailed(true);
                    setIsPhysicianListLoading(false);
                });
        }, [facilityId]);

        const getPhysiciansLinkedToLocation = useCallback(() => {
            const payload = {};
            CommonService._location.getPhysicianList(locationId, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response.data) {
                        setPhysiciansLinkedToLocation(response.data);
                    }
                })
                .catch((error: any) => {
                });
        }, [locationId]);

        useEffect(() => {
            if (facilityId) {
                getPhysicianList();
            }
            if (locationId) {
                getPhysiciansLinkedToLocation();
            }
        }, [getPhysicianList, getPhysiciansLinkedToLocation, facilityId, locationId])

        useEffect(() => {
            if (physiciansLinkedToLocation.length > 0) {
                const unLinkedPhysicians = _.differenceBy(completePhysicianList, physiciansLinkedToLocation, 'id');
                setPhysicianList(unLinkedPhysicians);
            } else {
                setPhysicianList(completePhysicianList);
            }
        }, [physiciansLinkedToLocation, completePhysicianList]);

        const onRemovePhysician = useCallback((physicianId: any) => {
            const payload = {
                "physicianId": physicianId,
                lastCompletedStep: lastCompletedStep
            };
            CommonService._location.unLinkPhysicianToLocation(locationId, payload)
                .then((response: IAPIResponseType<any>) => {
                    CommonService._alert.showToast(response?.message || `Physician has been unlinked from the location`, "success");
                    setPhysiciansLinkedToLocation((oldState: any) => {
                        const updatedList = _.cloneDeep(oldState);
                        const index = updatedList.findIndex((item: any) => item.id === physicianId);
                        updatedList.splice(index, 1);
                        return updatedList;
                    });
                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.error || `Error while unlinking the physician from the location`, "error");
                });
        }, [locationId, lastCompletedStep]);

        const handleRemovePhysician = useCallback((item: any) => {
            CommonService.openConfirmationDialog({
                confirmationTitle: 'Please confirm!',
                confirmationSubTitle: "Are you sure you want to remove this provider/physician?",
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.ConfirmationImage}`,
                direction: "up",
                yes: {
                    text: "Remove",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary",
                }
            })
                .then(() => {
                    onRemovePhysician(item);
                })
                .catch(() => {
                })
        }, [onRemovePhysician]);

        const handleLinkPhysician = useCallback((physicianId: any) => {
            const payload = {
                "physicianIds": physicianId
            };
            setIsLinking(true);
            CommonService._location.linkPhysicianToLocation(locationId, payload)
                .then((response: IAPIResponseType<any>) => {
                    setPhysiciansLinkedToLocation((oldState: any) => {
                        return [...oldState, ...response.data]
                    })
                    setPhysicianId([]);
                    setIsLinking(false);
                })
                .catch((error: any) => {
                    setIsLinking(false);
                    CommonService._alert.showToast(error?.message || 'Linking physician to location has been failed', "error");
                })
        }, [locationId]);

        return (
            <div className={'physician-details-component screen t-form-screen'}>
                {
                    isPhysicianListLoading && <div className="h-v-center">
                        <LottieFileGenarationComponent
                            loop={true}
                            autoplay={true}
                            animationData={ImageConfig.LoadingLottieFile}/>
                    </div>
                }
                {
                    !isPhysicianListLoading && isPhysicianListLoaded && <>
                        <FormTitleWithNavigationBack
                            title={'Provider/Physician'}
                            tooltipData={"Select the providers/physicians for this location from the list provided by your respective Facility. If no provider/physician is shown, please contact your facility."}
                            handleClick={prev}
                        />
                        <div className="t-form t-form-back-navigation">
                            <div className={'no-form'}>
                                <div className="t-form-controls-wrapper">
                                    <SelectDropdownComponent
                                        label={"Provider/Physician"}
                                        name={"physician"}
                                        placeholder={"Search Provider/Physician Name"}
                                        required={true}
                                        multiple={true}
                                        isDataLoaded={isPhysicianListLoaded}
                                        isDataLoadingFailed={isPhysicianListLoadingFailed}
                                        isDataLoading={isPhysicianListLoading}
                                        valueKey={"id"}
                                        onUpdate={(value: any) => {
                                            setPhysicianId(value)
                                        }}
                                        value={physicianId}
                                        options={physicianList}
                                        getOptionLabel={(physician => physician ? CommonService.physicianWithPrefix(physician) : '')}
                                    />
                                    <div className={"d-flex justify-content-flex-end"}>
                                        <ButtonComponent
                                            type="button"
                                            color={"secondary"}
                                            variant={"outlined"}
                                            fullWidth={false}
                                            isLoading={isLinking}
                                            disabled={physicianId?.length === 0}
                                            handleClick={() => {
                                                handleLinkPhysician(physicianId);
                                            }}
                                        >
                                            Link to Location
                                        </ButtonComponent>
                                    </div>
                                    <div className={'form-sub-title mrg-top-20'}>All Location Providers/Physicians</div>
                                    {physiciansLinkedToLocation.length === 0 && <FeedbackComponent className={"width-50"}>
                                        No Provider/Physician have been added to this location.
                                    </FeedbackComponent>
                                    }
                                    <div className={'physician-list-wrapper'}>
                                        {physiciansLinkedToLocation.map((physician: any, index: any) => {
                                                return (
                                                    <div key={index} className={'mrg-bottom-20'}>
                                                        {
                                                            index >= 0 &&
                                                            <AccordionComponent
                                                                // disabled={!values.physicianId}
                                                                isExpanded={physicianList[index]?.isPhysicianExpand}
                                                                title={CommonService.physicianWithPrefix(physician)}
                                                                subTitle={`(NPI: ${physician.npiNumber})`}
                                                                actions={<ButtonComponent
                                                                    startIcon={<RemoveCircleIcon/>}
                                                                    variant={"outlined"}
                                                                    fullWidth={false}
                                                                    type={'button'}
                                                                    size={'small'}
                                                                    color={"primary"}
                                                                    handleClick={
                                                                        () => {
                                                                            handleRemovePhysician(physician.id)
                                                                        }
                                                                    }

                                                                >
                                                                    Remove
                                                                </ButtonComponent>}
                                                                onChange={(isExpanded) => {
                                                                    if (isExpanded) {
                                                                        // setCurrentPhysicianIndex(index);
                                                                    }
                                                                    // setFieldValue(`physicianDetails[${index}].isPhysicianExpand`, isExpanded);
                                                                }}
                                                            >
                                                                <div
                                                                    className="physician-details-section">
                                                                    <div
                                                                        className={'d-flex justify-content-space-between mrg-top-15 align-items-center'}>
                                                                        <div className="form-sub-title">
                                                                            Personal Details
                                                                        </div>
                                                                    </div>
                                                                    <PhysicianDetailsComponent
                                                                        physician={physician}/>
                                                                </div>
                                                            </AccordionComponent>
                                                        }

                                                    </div>
                                                )
                                            }
                                        )
                                        }
                                    </div>
                                </div>
                                <div className="form-action-section">
                                    <ButtonComponent
                                        type="button"
                                        color={"primary"}
                                        variant={"outlined"}
                                        fullWidth={true}
                                        disabled={isLinking}
                                        handleClick={cancel}
                                    >
                                        Close
                                    </ButtonComponent>
                                    <ButtonComponent
                                        type="submit"
                                        color={"primary"}
                                        variant={"contained"}
                                        fullWidth={true}
                                        disabled={isLinking}
                                        handleClick={next}
                                    >
                                        {physiciansLinkedToLocation?.length > 0 ? "Next" : "Skip"}
                                    </ButtonComponent>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
        )
    }
;


export default LocationPhysicianAddComponent;
